/**
 * NBPLCMonthlyReport — NB Corporate Monthly Input + Live Output Control Center
 * Route: /corporate/nbplc/reporting
 *
 * Layout: Dangote-style two-column control center
 *   Left (3/5 cols):  Input form · Bulk upload · Action bar
 *   Right (2/5 cols): Live output panel · Always visible alongside form
 *     A. Baseline vs Current vs Reduction
 *     B. Scope 1/2/3 Breakdown
 *     C. Circularity Metrics
 *     D. Circularity Value
 *     E. Calculation Transparency
 *
 * Logic/SSOT/Upload: UNCHANGED — pure layout refactor only.
 */
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useNBPLC } from "@/context/NBPLCContext";
import { PageHeader, SectionCard } from "@/components/ds";
import {
  RefreshCw, Save, CheckCircle2, AlertTriangle, Upload,
  Download, FileSpreadsheet, ChevronDown, ChevronUp, ArrowRight
} from "lucide-react";
import client from "@/api/client";
import * as XLSX from "xlsx";

// ── Field schema ──────────────────────────────────────────────────────────────
const FIELD_SECTIONS = [
  {
    key:"energy", label:"Energy Consumption", borderColor:"border-orange-400",
    fields:[
      {key:"electricity_kwh",      label:"Grid electricity",     unit:"kWh",     required:true,  tip:"Monthly kWh from utility bills"},
      {key:"solar_kwh",            label:"Solar generation",     unit:"kWh",     required:false, tip:"Metered solar output"},
      {key:"diesel_litres",        label:"Diesel consumed",      unit:"litres",  required:true,  tip:"Boiler / generator diesel"},
      {key:"natural_gas_m3",       label:"Natural gas",          unit:"Nm³",     required:false, tip:"Gas meter reading"},
      {key:"biomass_t",            label:"Biomass fuel",         unit:"tonnes",  required:false, tip:"Agricultural residues"},
    ],
  },
  {
    key:"production", label:"Production & Water", borderColor:"border-blue-400",
    fields:[
      {key:"production_hl",        label:"Beer production",      unit:"hl",      required:true,  tip:"Total hectolitres produced"},
      {key:"water_used_hl",        label:"Water consumption",    unit:"hl",      required:true,  tip:"Total water across plant"},
    ],
  },
  {
    key:"packaging", label:"Packaging", borderColor:"border-purple-400",
    fields:[
      {key:"glass_bottles_units",  label:"Glass bottles",        unit:"units",   required:true,  tip:"New glass bottles received"},
      {key:"cans_units",           label:"Aluminium cans",       unit:"units",   required:false, tip:"Can purchases"},
      {key:"pet_bottles_units",    label:"PET bottles",          unit:"units",   required:false, tip:"PET bottle purchases"},
      {key:"returnable_bottles_pct",label:"Returnable rate",     unit:"%",       required:false, tip:"% volume in returnable bottles"},
    ],
  },
  {
    key:"waste", label:"Waste & Circularity", borderColor:"border-emerald-400",
    fields:[
      {key:"waste_generated_t",    label:"Solid waste",          unit:"tonnes",  required:false, tip:"Spent grain, sludge, packaging"},
      {key:"waste_recycled_pct",   label:"Waste recycled",       unit:"%",       required:false, tip:"% diverted from landfill"},
      {key:"biogas_kwh",           label:"Biogas generated",     unit:"kWh",     required:false, tip:"Anaerobic digestion output"},
    ],
  },
  {
    key:"logistics", label:"Logistics", borderColor:"border-slate-400",
    fields:[
      {key:"transport_tkm",        label:"Outbound distribution",unit:"tonne-km",required:false, tip:"Product tonnes × delivery km"},
    ],
  },
  {
    key:"ingredients", label:"Agricultural Inputs", borderColor:"border-amber-400",
    fields:[
      {key:"sorghum_t",            label:"Sorghum purchased",    unit:"tonnes",  required:false, tip:"Local sorghum"},
      {key:"barley_t",             label:"Malted barley",        unit:"tonnes",  required:false, tip:"Imported malted barley"},
    ],
  },
  {
    key:"refrigerants", label:"Refrigerants", borderColor:"border-red-400",
    fields:[
      {key:"refrigerant_r134a_kg", label:"R-134a top-up",        unit:"kg",      required:false, tip:"GWP=1430"},
      {key:"refrigerant_r410a_kg", label:"R-410A top-up",        unit:"kg",      required:false, tip:"GWP=2088"},
      {key:"refrigerant_r32_kg",   label:"R-32 top-up",          unit:"kg",      required:false, tip:"GWP=675"},
      {key:"refrigerant_nh3_kg",   label:"NH₃ top-up",           unit:"kg",      required:false, tip:"GWP≈0"},
    ],
  },
];

const ALL_FIELDS = FIELD_SECTIONS.flatMap(s => s.fields);

const PLANTS = [
  {asset_id:"AST-NB-IB", bu_id:"BU-NB-001", name:"NB Ibadan Brewery",  location:"Ibadan"},
  {asset_id:"AST-NB-LA", bu_id:"BU-NB-002", name:"NB Lagos Brewery",   location:"Lagos"},
  {asset_id:"AST-NB-AB", bu_id:"BU-NB-003", name:"NB Aba Brewery",     location:"Aba"},
  {asset_id:"AST-NB-KD", bu_id:"BU-NB-004", name:"NB Kaduna Brewery",  location:"Kaduna"},
  {asset_id:"AST-NB-EN", bu_id:"BU-NB-005", name:"NB Enugu Brewery",   location:"Enugu"},
];

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmtV = (v) => v >= 1_000_000 ? `$${(v/1e6).toFixed(1)}M` : `$${Math.round(v/1000)}K`;

function Collapsible({ title, badge="", children, defaultOpen=true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden">
      <button onClick={() => setOpen(v=>!v)}
        className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100 text-left transition-colors">
        <div className="flex items-center gap-2">
          <p className="text-[10px] font-bold text-slate-700">{title}</p>
          {badge && <span className="text-[8px] font-bold px-1.5 py-0.5 rounded-full bg-teal-100 text-teal-700 border border-teal-200">{badge}</span>}
        </div>
        {open ? <ChevronUp size={12} className="text-slate-400"/> : <ChevronDown size={12} className="text-slate-400"/>}
      </button>
      {open && <div className="p-4">{children}</div>}
    </div>
  );
}

function kv(label, value, sub="", color="text-slate-800") {
  return (
    <div className="flex items-center justify-between py-1.5 border-b border-slate-50 last:border-0">
      <span className="text-[10px] text-slate-500">{label}</span>
      <div className="text-right">
        <span className={`text-[10px] font-bold ${color}`}>{value}</span>
        {sub && <p className="text-[9px] text-slate-400">{sub}</p>}
      </div>
    </div>
  );
}

function downloadTemplate() {
  const ws = XLSX.utils.aoa_to_sheet([ALL_FIELDS.map(f => f.key), ALL_FIELDS.map(f => f.required ? "0" : "")]);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "NB Monthly Data");
  XLSX.writeFile(wb, "NB_Monthly_Template.xlsx");
}

// ── Right panel: live output ──────────────────────────────────────────────────
function OutputPanel({ calcResult, baselineData, form }) {
  const s1 = calcResult?.scope1?.total_tco2e || 0;
  const s2 = calcResult?.scope2?.electricity_tco2e || 0;
  const s3 = calcResult?.scope3?.total_tco2e || 0;
  const total = calcResult?.total_tco2e || 0;
  const kpis = calcResult?.kpis || {};
  const circ = calcResult?.circularity || {};
  const circVal = calcResult?.circularity_valuation || {};
  const prevTotal = baselineData?.total_tco2e || 0;
  const delta = prevTotal > 0 ? total - prevTotal : 0;

  // Live estimates from form (pre-calculation, always visible)
  const elec = form.electricity_kwh || 0;
  const sol  = form.solar_kwh || 0;
  const die  = form.diesel_litres || 0;
  const prod = form.production_hl || 1;
  const water= form.water_used_hl || 0;
  const liveS2  = (elec * 0.43 / 1000).toFixed(1);
  const liveS1  = (die * 2.68 / 1000).toFixed(1);
  const liveEI  = prod > 1 ? ((elec+sol)/prod).toFixed(1) : "—";
  const liveWI  = prod > 1 ? (water/prod).toFixed(2) : "—";
  const liveRen = elec+sol > 0 ? (sol/(elec+sol)*100).toFixed(1) : "0";
  const liveCO2 = prod > 1 ? (((parseFloat(liveS2)+parseFloat(liveS1))*1000)/prod).toFixed(1) : "—";

  if (!calcResult || calcResult.error) {
    // Pre-calculation: show live estimates
    return (
      <div className="space-y-3">
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
          <p className="text-[9px] font-bold text-teal-700 uppercase tracking-wide mb-3">Live Brewery Estimates</p>
          <p className="text-[9px] text-slate-400 italic mb-3">Updating as you type — click Calculate for full analysis</p>
          {[
            ["Scope 2 (electricity)",  `${liveS2} tCO₂e`,    parseFloat(liveS2) > 5000 ? "High" : ""],
            ["Scope 1 (diesel)",       `${liveS1} tCO₂e`,    ""],
            ["Energy intensity",       `${liveEI} kWh/hl`,   liveEI !== "—" && parseFloat(liveEI) > 70 ? "↑ above BIER 70" : ""],
            ["Water intensity",        `${liveWI} hl/hl`,    liveWI !== "—" && parseFloat(liveWI) > 3.5 ? "↑ above BIER 3.5" : ""],
            ["Renewable share",        `${liveRen}%`,         ""],
            ["Emission intensity",     `${liveCO2} kgCO₂e/hl`,liveCO2 !== "—" && parseFloat(liveCO2) > 20 ? "↑ above BIER 20" : ""],
          ].map(([l,v,note]) => (
            <div key={l} className="flex items-center justify-between py-1.5 border-b border-slate-50 last:border-0">
              <div><p className="text-[10px] text-slate-500">{l}</p>
              {note && <p className="text-[9px] text-amber-600 italic">{note}</p>}</div>
              <p className="text-[10px] font-bold text-slate-800">{v}</p>
            </div>
          ))}
        </div>
        <div className="bg-slate-900 rounded-xl p-4 text-center">
          <p className="text-[10px] text-slate-400">Click <strong className="text-white">Calculate Emissions</strong> for full output</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="output-panel">

      {/* A. Baseline vs Current */}
      <Collapsible title="A. Baseline vs Current" badge="SSOT">
        <div className="space-y-2">
          <div className="grid grid-cols-3 gap-2">
            {[
              {l:"Baseline",    v: prevTotal > 0 ? `${(prevTotal/1000).toFixed(1)}K` : "—",  color:"text-slate-600", bg:"bg-slate-50"},
              {l:"Current",     v:`${(total/1000).toFixed(1)}K`,      color:"text-slate-900 font-bold", bg:"bg-white border border-slate-300"},
              {l: delta < 0 ? "Reduction" : delta > 0 ? "Increase" : "Change",
               v: prevTotal > 0 ? `${delta < 0 ? "−" : "+"}${(Math.abs(delta)/1000).toFixed(1)}K` : "—",
               color: delta < 0 ? "text-emerald-700 font-bold" : delta > 0 ? "text-red-600 font-bold" : "text-slate-400",
               bg: delta < 0 ? "bg-emerald-50" : delta > 0 ? "bg-red-50" : "bg-slate-50"},
            ].map(c => (
              <div key={c.l} className={`rounded-xl p-3 text-center ${c.bg}`}>
                <p className="text-[8px] text-slate-400 mb-1">{c.l}</p>
                <p className={`text-sm font-bold ${c.color}`}>{c.v}</p>
                <p className="text-[8px] text-slate-400">tCO₂e</p>
              </div>
            ))}
          </div>
          {!baselineData?.has_baseline && <p className="text-[9px] text-slate-400 italic">No previous period data.</p>}
        </div>
      </Collapsible>

      {/* B. Scope 1/2/3 */}
      <Collapsible title="B. Scope 1 / 2 / 3">
        {kv("Total", `${total.toFixed(1)} tCO₂e`, "", "text-slate-900")}
        <div className="mt-2 space-y-1.5">
          {[
            {label:"Scope 1 — Direct",  val:s1,  color:"text-orange-600",
             sub: [{k:"Diesel",     v:calcResult.scope1?.diesel_tco2e?.toFixed(1)||"0"},
                   {k:"Natural gas",v:calcResult.scope1?.natgas_tco2e?.toFixed(1)||"0"},
                   {k:"Refrigerants",v:calcResult.scope1?.refrigerant_tco2e?.toFixed(2)||"0"}]},
            {label:"Scope 2 — Electricity", val:s2, color:"text-blue-600",
             sub: [{k:"Grid electricity",v:s2.toFixed(1)},
                   {k:"Renewable %",    v:(calcResult.scope2?.renewable_pct||0)+"%"}]},
            {label:"Scope 3 — Indirect", val:s3, color:"text-purple-600",
             sub: [{k:"Packaging",    v:calcResult.scope3?.packaging_tco2e?.toFixed(1)||"0"},
                   {k:"Logistics",    v:calcResult.scope3?.transport_tco2e?.toFixed(1)||"0"},
                   {k:"Agriculture",  v:calcResult.scope3?.agriculture_tco2e?.toFixed(1)||"0"}]},
          ].map(scope => (
            <div key={scope.label} className="bg-slate-50 rounded-lg px-3 py-2">
              <div className="flex justify-between items-center mb-1">
                <p className="text-[9px] font-bold text-slate-600">{scope.label}</p>
                <p className={`text-[10px] font-bold ${scope.color}`}>{scope.val.toFixed(1)} tCO₂e</p>
              </div>
              <div className="space-y-0.5">
                {scope.sub.map(({k,v}) => (
                  <div key={k} className="flex justify-between text-[9px]">
                    <span className="text-slate-400">{k}</span>
                    <span className="text-slate-600 font-medium">{v}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </Collapsible>

      {/* C. Circularity Metrics */}
      <Collapsible title="C. Circularity Metrics" defaultOpen={false}>
        {[
          ["Returnable rate",    `${circ.returnable_pct||0}%`,              "Target 70%",  (circ.returnable_pct||0)>=70],
          ["Waste recycled",     `${circ.waste_recycled_pct||0}%`,          "Target 80%",  (circ.waste_recycled_pct||0)>=80],
          ["Renewable energy",   `${circ.renewable_pct||0}%`,              "Target 30%",  (circ.renewable_pct||0)>=30],
          ["Water intensity",    `${kpis.water_intensity_hl_hl?.toFixed(2)||"—"} hl/hl`, "BIER 3.5", (kpis.water_intensity_hl_hl||99)<=3.5],
          ["Energy intensity",   kpis.energy_intensity_kwh_hl ? `${kpis.energy_intensity_kwh_hl.toFixed(0)} kWh/hl` : "—", "BIER 70", (kpis.energy_intensity_kwh_hl||999)<=70],
          ["Emission intensity", kpis.emission_intensity_kg_hl ? `${kpis.emission_intensity_kg_hl.toFixed(1)} kg/hl` : "—", "BIER 20", (kpis.emission_intensity_kg_hl||999)<=20],
        ].map(([l,v,bench,ok]) => (
          <div key={l} className="flex items-center justify-between py-1.5 border-b border-slate-50 last:border-0">
            <div><p className="text-[10px] text-slate-500">{l}</p><p className="text-[8px] text-slate-400">{bench}</p></div>
            <span className={`text-[10px] font-bold ${ok ? "text-emerald-700" : "text-amber-600"}`}>{v}</span>
          </div>
        ))}
      </Collapsible>

      {/* D. Circularity Value */}
      {circVal.total_value_usd_yr > 0 && (
        <Collapsible title="D. Circularity Value" defaultOpen={false}>
          <p className="text-[9px] text-slate-400 italic mb-2">Derived from NB engine only — no fabricated economics.</p>
          {circVal.returnable_savings_usd_yr > 0 && kv("Returnable reuse", `${fmtV(circVal.returnable_savings_usd_yr*12)}/yr`, "", "text-teal-700")}
          {circVal.waste_savings_usd_yr > 0 && kv("Waste diversion", `${fmtV(circVal.waste_savings_usd_yr*12)}/yr`, "", "text-teal-700")}
          {circVal.solar_savings_usd_yr > 0 && kv("Solar/biogas value", `${fmtV(circVal.solar_savings_usd_yr*12)}/yr`, "", "text-teal-700")}
          <div className="pt-1 border-t border-slate-100 mt-1">
            {kv("Total (annualised)", `${fmtV(circVal.total_value_usd_yr)}/yr`, "", "text-teal-700")}
          </div>
        </Collapsible>
      )}

      {/* E. Calculation Transparency */}
      <Collapsible title="E. Calculation Transparency" defaultOpen={false}>
        {[
          ["Protocol",         "DCI-BREWERY-V1.0"],
          ["Standard",         "IPCC 2006 + GHG Protocol + BIER"],
          ["Scope 2 factor",   "0.43 kgCO₂/kWh (Nigeria IEA 2023)"],
          ["Diesel factor",    "2.68 kgCO₂/litre"],
          ["Gas factor",       "2.04 kgCO₂/Nm³"],
          ["GWP basis",        "IPCC AR4 (R-134a=1430, R-410A=2088)"],
          ["Calc hash",        calcResult.calc_hash ? calcResult.calc_hash.slice(0,12)+"..." : "—"],
        ].map(([l,v]) => kv(l, v))}
      </Collapsible>

    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function NBPLCMonthlyReport() {
  const navigate = useNavigate();
  const { selectedPeriod, period, periods, setSelectedPeriod, refresh } = useNBPLC();
  const [selectedPlant, setSelectedPlant] = useState(PLANTS[0].asset_id);
  const [form, setForm] = useState({});
  const [activeTab, setActiveTab] = useState("input");
  const [loadingForm, setLoadingForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [calculating, setCalc] = useState(false);
  const [calcResult, setCalcResult] = useState(null);
  const [submissionStatus, setSubmissionStatus] = useState("not_started");
  const [uploadStatus, setUploadStatus] = useState(null);
  const [baselineData, setBaselineData] = useState(null);
  const [dragging, setDragging] = useState(false);
  const fileRef = useRef();

  const plant = PLANTS.find(p => p.asset_id === selectedPlant);
  const requiredFields = ALL_FIELDS.filter(f => f.required);
  const filledRequired = requiredFields.filter(f => form[f.key] !== undefined && form[f.key] !== "" && form[f.key] !== 0).length;
  const readyToCalc = filledRequired >= requiredFields.length;

  // SSOT: Pre-load existing submission data — unchanged logic
  useEffect(() => {
    if (!selectedPlant || !selectedPeriod) return;
    setLoadingForm(true); setCalcResult(null);
    client.get(`/nbplc/submission/${selectedPlant}/${selectedPeriod}`)
      .then(r => {
        if (r.data.has_data && Object.keys(r.data.activity_data).length > 0) {
          setForm(r.data.activity_data);
          setSubmissionStatus(r.data.status || "submitted");
        } else { setForm({}); setSubmissionStatus("not_started"); }
      })
      .catch(() => setForm({}))
      .finally(() => setLoadingForm(false));
    client.get(`/nbplc/calculation/${selectedPlant}/${selectedPeriod}`)
      .then(r => { if (r.data.has_result) setCalcResult(r.data); }).catch(()=>{});
    client.get(`/nbplc/baseline/${selectedPlant}/${selectedPeriod}`)
      .then(r => { if (r.data.has_baseline) setBaselineData(r.data); }).catch(()=>{});
  }, [selectedPlant, selectedPeriod]);

  const handleChange = (key, val) => setForm(prev => ({ ...prev, [key]: parseFloat(val) || 0 }));

  const handleSave = async () => {
    setSaving(true);
    await new Promise(r => setTimeout(r, 600));
    setSaving(false); setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  const handleCalculate = async () => {
    if (!readyToCalc) return;
    setCalc(true);
    try {
      const resp = await client.post(`/nbplc/calculate/${selectedPlant}/${selectedPeriod}`, form);
      setCalcResult({ has_result: true, ...resp.data });
      setSubmissionStatus("calculated");
      refresh();
      client.get(`/nbplc/baseline/${selectedPlant}/${selectedPeriod}`)
        .then(r => { if (r.data.has_baseline) setBaselineData(r.data); }).catch(()=>{});
    } catch (e) {
      setCalcResult({ error: e.message || "Calculation failed" });
    } finally { setCalc(false); }
  };

  const handleFile = useCallback(async (file) => {
    if (!file) return;
    setUploadStatus({ loading: true });
    try {
      const formData = new FormData();
      formData.append("file", file);
      const resp = await client.post("/nbplc/parse-upload", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      if (resp.data.success) {
        setForm(prev => ({ ...prev, ...resp.data.mapped_fields }));
        setActiveTab("input");
        setUploadStatus({ success: true, fieldCount: resp.data.field_count, message: resp.data.message, errors: resp.data.errors || [] });
      } else {
        setUploadStatus({ success: false, errors: resp.data.errors || ["Upload failed"] });
      }
    } catch (e) {
      setUploadStatus({ success: false, errors: [e.message || "Upload failed"] });
    }
  }, []);

  const handleDrop = (e) => {
    e.preventDefault(); setDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  };

  return (
    <div className="p-6 lg:p-8 space-y-5">

      {/* ── HEADER ─────────────────────────────────────────────────────── */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-0.5">Nigeria Breweries Plc</p>
          <PageHeader title="Monthly Reporting" />
          <p className="text-xs text-slate-400 mt-0.5">
            {period?.label} · {plant?.name} · Single source of truth for all downstream pages
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select value={selectedPeriod||""} onChange={e=>setSelectedPeriod(e.target.value)}
            className="text-xs border border-slate-200 rounded-lg px-2.5 py-1.5 bg-white" data-testid="period-selector">
            {periods.map(p=><option key={p.period_id} value={p.period_id}>{p.label}</option>)}
          </select>
          <select value={selectedPlant} onChange={e => { setSelectedPlant(e.target.value); setForm({}); setCalcResult(null); setUploadStatus(null); }}
            className="text-xs border border-slate-200 rounded-lg px-2.5 py-1.5 bg-white" data-testid="plant-selector">
            {PLANTS.map(p=><option key={p.asset_id} value={p.asset_id}>{p.name.replace("NB ","")}</option>)}
          </select>
          <span className={`text-xs font-bold px-2 py-0.5 rounded-full border ${
            submissionStatus === "submitted"  ? "bg-teal-100 text-teal-700 border-teal-200" :
            submissionStatus === "calculated" ? "bg-blue-100 text-blue-700 border-blue-200" :
            "bg-slate-100 text-slate-500 border-slate-200"
          }`} data-testid="status-badge">
            {loadingForm ? "Loading…" :
             submissionStatus === "submitted"  ? "Pre-filled from SSOT" :
             submissionStatus === "calculated" ? "Calculated" : "Draft"}
          </span>
        </div>
      </div>

      {/* ── MAIN TWO-COLUMN LAYOUT (Dangote-style) ────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">

        {/* ── LEFT COLUMN (3/5): Input + Upload + Actions ────────────── */}
        <div className="lg:col-span-3 space-y-4">

          {/* Tab switcher */}
          <div className="flex gap-2 border-b border-slate-200">
            {[["input","Manual Input"],["upload","Bulk Upload"]].map(([tab,label])=>(
              <button key={tab} onClick={() => setActiveTab(tab)}
                className={`px-4 py-2 text-xs font-semibold border-b-2 transition-colors ${
                  activeTab===tab ? "border-teal-500 text-teal-700" : "border-transparent text-slate-400 hover:text-slate-700"
                }`}>
                {label}
              </button>
            ))}
          </div>

          {/* Manual Input Form */}
          {activeTab === "input" && (
            <>
              {/* Progress */}
              <div className="bg-white border border-slate-200 rounded-xl p-3">
                <div className="flex justify-between text-xs mb-1.5">
                  <span className="font-semibold text-slate-700">Required fields</span>
                  <span className={`font-bold ${readyToCalc?"text-emerald-700":"text-amber-600"}`}>{filledRequired}/{requiredFields.length}</span>
                </div>
                <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full rounded-full bg-emerald-500 transition-all" style={{width:`${filledRequired/requiredFields.length*100}%`}}/>
                </div>
              </div>

              {/* Field sections */}
              {FIELD_SECTIONS.map(sec => (
                <SectionCard key={sec.key}>
                  <p className={`text-xs font-bold text-slate-700 uppercase tracking-wide mb-3 pb-2 border-b-2 ${sec.borderColor} border-b`}>
                    {sec.label}
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    {sec.fields.map(f => (
                      <div key={f.key}>
                        <label className="text-[9px] font-semibold text-slate-600 flex items-center gap-1 mb-1">
                          {f.label} <span className="text-slate-400">({f.unit})</span>
                          {f.required && <span className="text-red-500 font-bold">*</span>}
                        </label>
                        <input type="number" min="0"
                          value={form[f.key] !== undefined ? form[f.key] : ""}
                          onChange={e => handleChange(f.key, e.target.value)}
                          placeholder={`Enter ${f.unit}`}
                          className="w-full px-2.5 py-1.5 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-teal-400"/>
                        <p className="text-[8px] text-slate-400 mt-0.5">{f.tip}</p>
                      </div>
                    ))}
                  </div>
                </SectionCard>
              ))}
            </>
          )}

          {/* Bulk Upload */}
          {activeTab === "upload" && (
            <div className="space-y-4">
              <div className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-xl px-4 py-3">
                <div>
                  <p className="text-xs font-bold text-blue-800">NB Monthly Template</p>
                  <p className="text-[10px] text-blue-600">All required and optional NB brewery fields</p>
                </div>
                <button onClick={downloadTemplate}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white text-[10px] font-bold rounded-lg hover:bg-blue-700">
                  <Download size={11}/> Download .xlsx
                </button>
              </div>
              <div
                onDragEnter={e=>{e.preventDefault();setDragging(true);}}
                onDragOver={e=>{e.preventDefault();setDragging(true);}}
                onDragLeave={()=>setDragging(false)}
                onDrop={handleDrop}
                className={`border-2 border-dashed rounded-xl p-10 text-center transition-colors ${
                  dragging ? "border-teal-400 bg-teal-50" : "border-slate-300 bg-slate-50/50 hover:border-teal-300"
                }`} data-testid="upload-dropzone">
                <FileSpreadsheet size={28} className="text-slate-400 mx-auto mb-2"/>
                <p className="text-sm font-semibold text-slate-600 mb-1">Drop Excel or CSV here</p>
                <p className="text-[10px] text-slate-400 mb-3">Accepts .xlsx, .xls, .csv</p>
                <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" className="hidden"
                  onChange={e => { if (e.target.files?.[0]) handleFile(e.target.files[0]); }}/>
                <button onClick={() => fileRef.current?.click()}
                  className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#0F172A] text-white text-xs font-bold rounded-xl hover:bg-slate-800"
                  data-testid="browse-files-btn">
                  <Upload size={11}/> Browse Files
                </button>
              </div>
              {uploadStatus && (
                <div className={`rounded-xl px-4 py-3 border ${
                  uploadStatus.loading ? "bg-slate-50 border-slate-200" :
                  uploadStatus.success ? "bg-emerald-50 border-emerald-200" : "bg-red-50 border-red-200"
                }`} data-testid="upload-status">
                  {uploadStatus.loading ? (
                    <p className="text-xs text-slate-600 flex items-center gap-2"><RefreshCw size={12} className="animate-spin"/> Parsing…</p>
                  ) : uploadStatus.success ? (
                    <div>
                      <p className="text-xs font-bold text-emerald-700 flex items-center gap-2">
                        <CheckCircle2 size={13}/> {uploadStatus.fieldCount} fields mapped — switched to Input tab
                      </p>
                      {uploadStatus.errors?.length > 0 && uploadStatus.errors.map((e,i) => <p key={i} className="text-[9px] text-amber-600">{e}</p>)}
                    </div>
                  ) : <p className="text-xs font-bold text-red-700">{uploadStatus.errors?.join(", ")}</p>}
                </div>
              )}
            </div>
          )}

          {/* Action bar */}
          <div className="flex items-center gap-3">
            <button onClick={handleSave} disabled={saving}
              className="flex items-center gap-2 px-4 py-2.5 bg-white border border-slate-200 text-slate-700 text-xs font-bold rounded-xl hover:border-teal-400"
              data-testid="save-draft-btn">
              {saving ? <RefreshCw size={12} className="animate-spin"/> : saved ? <CheckCircle2 size={12} className="text-emerald-500"/> : <Save size={12}/>}
              {saved ? "Saved!" : "Save Draft"}
            </button>
            <button onClick={handleCalculate} disabled={!readyToCalc || calculating}
              className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-[#0F172A] text-white text-xs font-bold rounded-xl hover:bg-slate-800 disabled:opacity-50"
              data-testid="calculate-btn">
              {calculating ? <><RefreshCw size={12} className="animate-spin"/> Calculating…</> : "Calculate Emissions →"}
            </button>
          </div>
        </div>

        {/* ── RIGHT COLUMN (2/5): Live Output Panel ──────────────────── */}
        <div className="lg:col-span-2">
          <div className="lg:sticky lg:top-6 space-y-3">
            <div className="flex items-center gap-2 pb-2 border-b border-slate-200">
              <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">Live Output Intelligence</p>
              {calcResult && !calcResult.error && (
                <span className="flex items-center gap-1 text-[9px] text-teal-600 font-semibold">
                  <CheckCircle2 size={9}/> Updated
                </span>
              )}
            </div>
            <OutputPanel calcResult={calcResult} baselineData={baselineData} form={form} />
          </div>
        </div>
      </div>

      {/* ── DOWNSTREAM LINKS (below main content) ─────────────────────── */}
      <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
        <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-3">
          Downstream pages — reading from this month's output
        </p>
        <div className="flex flex-wrap gap-2">
          {[
            ["Dashboard",      "/corporate/nbplc/dashboard"],
            ["Climate",        "/corporate/nbplc/climate"],
            ["SDG",            "/corporate/nbplc/sdg"],
            ["Value Bridge",   "/corporate/nbplc/value-bridge"],
            ["Strategy",       "/corporate/nbplc/carbon-strategy"],
            ["Structuring",    "/corporate/nbplc/carbon-structuring"],
            ["Financials",     "/corporate/nbplc/financing"],
            ["Reports",        "/corporate/nbplc/reports"],
          ].map(([label, path]) => (
            <button key={path} onClick={() => navigate(path)}
              className="flex items-center gap-1 px-3 py-1.5 bg-white border border-slate-200 text-slate-600 text-[10px] font-semibold rounded-lg hover:border-teal-300 hover:text-teal-700 transition-colors">
              {label} <ArrowRight size={9}/>
            </button>
          ))}
        </div>
        {calcResult && !calcResult.error && (
          <p className="text-[9px] text-teal-600 font-semibold mt-2 flex items-center gap-1">
            <CheckCircle2 size={9}/> All pages updated — no manual refresh needed
          </p>
        )}
      </div>
    </div>
  );
}
